import { FC } from 'react';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import { mainColors } from '../../../../styling/theme';
import { percentageToTwoDecimalPlaces } from '../../../../utilities/numberFormatters';
import GenericLineChart from '../../../charts/GenericLineChart';
import GridItem from '../../../layout/GridComponents/GridItem';
import { Typography } from '@mui/material';
import { SubscriptionsRedemptionsReport } from './subscriptionsRedemptions.types';

interface subscriptionsRedemptionsChartProps {
  data: SubscriptionsRedemptionsReport;
  title: string;
}

function buildSubscriptionsRedemptionsData(
  subsRedsData: SubscriptionsRedemptionsReport,
  title: string,
) {
  const chartData: { name: string; value: number }[] = [];
  if (title === 'Redemption Data') {
    const rows = subsRedsData.redemptions.slice(1);
    rows.forEach((value) => {
      chartData.push({
        name: value[0],
        value: value[1] as number,
      });
    });
  } else if (title === 'Gross Activity Data') {
    const rows = subsRedsData.gross_activity.slice(1);
    rows.forEach((value) => {
      chartData.push({
        name: value[0],
        value: value[1] as number,
      });
    });
  }

  return chartData;
}

const SubscriptionsRedemptionsChart: FC<subscriptionsRedemptionsChartProps> = (
  props,
) => {
  const { data, title } = props;
  const chartData = buildSubscriptionsRedemptionsData(data, title);
  const { height } = useWindowSize();
  if (chartData.length === 0) {
    return (
      <GridItem xs={6} card style={{ padding: 8 }}>
        <Typography variant="h2" style={{ marginLeft: '1.25rem', marginTop: '0.62rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" style={{ padding: '1rem' }}>
          No chart data.
        </Typography>
      </GridItem>
    );
  }
  let minvalue = chartData[0].value;
  let maxvalue = chartData[0].value;
  chartData.forEach((value) => {
    if (minvalue > value.value) minvalue = value.value;
    if (maxvalue < value.value) maxvalue = value.value;
  });

  return (
    <GridItem xs={6} card style={{ padding: 8 }}>
      <Typography
        variant="h2"
        style={{
          marginLeft: '1.25rem',
          marginTop: '0.62rem',
          fontSize: '1.25rem',
          fontWeight: 400,
        }}
      >
        {title}
      </Typography>

      <GenericLineChart
        title={title}
        data={chartData}
        export
        id={
          title === 'Redemption Data'
            ? 'redemption_chart'
            : 'subscription_chart'
        }
        xAxisDataKey="name"
        yAxisDomain={[
          minvalue === 0 ? -0.01 : minvalue - Math.abs(minvalue) * 0.1,
          maxvalue + Math.abs(maxvalue) * 0.1,
        ]}
        yAxisTickFormatter={(value: number) =>
          percentageToTwoDecimalPlaces(value)
        }
        tooltipFormatter={(value: number) =>
          percentageToTwoDecimalPlaces(value)
        }
        lines={[
          {
            dataKey: 'value',
            color:
              title === 'Redemption Data'
                ? mainColors.mainBlue_lighter
                : mainColors.Pass,
          },
        ]}
        width={'100%'}
        height={height / 2 - 160}
        showDot={false}
        margin={{
          top: 10,
          bottom: 10,
          left: 10,
          right: 40,
        }}
        error={'No Subcriptions/Redemptions Data available'}
      />
    </GridItem>
  );
};

export default SubscriptionsRedemptionsChart;
